/**
 * Render markdown (review intros, the Drivers intro, Guide docs and answers) to
 * HTML. Raw HTML is escaped before parsing, so only markdown syntax renders, and
 * every link/image URL in the result is checked against an allow-list (security
 * audit Oct 2026: the old one-pass `javascript:` strip was bypassable, e.g.
 * `javajavascript:script:` or an HTML entity, and gave a live script link).
 */
import { marked } from 'marked';

/** http(s), mailto, and in-app links ("/expenses", "#part"); nothing else. */
function safeUrl(raw: string): boolean {
  const v = raw.trim();
  if (v.startsWith('/') && !v.startsWith('//')) return true;
  if (v.startsWith('#')) return true;
  try {
    return ['http:', 'https:', 'mailto:'].includes(new URL(v).protocol);
  } catch {
    return false;
  }
}

export function renderMarkdown(md: string): string {
  const escaped = (md ?? '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const html = marked.parse(escaped, { async: false }) as string;
  // Check the URLs AFTER the browser decoded them (entities, mixed case).
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  for (const el of Array.from(doc.querySelectorAll('[href], [src]'))) {
    for (const attr of ['href', 'src']) {
      const val = el.getAttribute(attr);
      if (val !== null && !safeUrl(val)) el.removeAttribute(attr);
    }
  }
  for (const a of Array.from(doc.querySelectorAll('a[href]'))) {
    const href = a.getAttribute('href') ?? '';
    if (/^https?:/i.test(href)) a.setAttribute('rel', 'noopener noreferrer');
  }
  return doc.body.firstElementChild?.innerHTML ?? '';
}
